import { ContractBillingScheduleProduct, GetContractBillingScheduleResponse } from '@vy/api-definition';
import { InvoiceReviewStatus } from '@vy/types';
import {
  first,
  getMapEntries,
  groupBy,
  ItemOf,
  newArrayOfLength,
  sortDescending,
  validateNotEmptyArray,
  validateNotEmptyObject,
  validateNotNullOrUndefined,
  vyDate,
} from '@vy/utils';
import { RevenueItem } from './ContractBillingScheduleChart';

type ProductWithOriginalStatus = ContractBillingScheduleProduct & {
  originalInvoiceStatus?: InvoiceReviewStatus;
  originalInvoiceData?: {
    id: string;
  };
};

function validateProductWithOriginalStatus(
  product: ContractBillingScheduleProduct,
): asserts product is ProductWithOriginalStatus {
  validateNotEmptyObject(product, 'Product');
  validateNotNullOrUndefined(product.productId);
  validateNotNullOrUndefined(product.name);
  validateNotNullOrUndefined(product.period);
  validateNotNullOrUndefined(product.revenueBreakdown);
}

function buildProductUniqueKey(product: ProductWithOriginalStatus, productIndex: number): string {
  const status = product.originalInvoiceStatus;
  const invoiceId = product.originalInvoiceData?.id;

  return `${product.name}_${status}_${invoiceId}_${productIndex}`;
}

export function buildProductRevenueData(
  billingScheduleData: GetContractBillingScheduleResponse['invoices'],
): RevenueItem[] {
  return billingScheduleData.flatMap((invoice) =>
    invoice.products.map((product, productIndex) => {
      validateProductWithOriginalStatus(product);

      const status = product.originalInvoiceStatus ?? invoice.reviewStatus;
      const uniqueKey = buildProductUniqueKey(product, productIndex);

      return {
        name: product.name,
        invoicingDate: invoice.invoicingDate.toISOString(),
        status,
        totalRevenue: product.revenueBreakdown.total,
        uniqueKey,
      };
    }),
  );
}

export function buildChartData(
  billingScheduleData: GetContractBillingScheduleResponse['invoices'],
): Record<string, number | string>[] {
  const groupedData = groupBy(billingScheduleData, (invoice) =>
    vyDate(invoice.invoicingDate).format('MMM YYYY'),
  );

  return getMapEntries(groupedData).map(([key, data]) => {
    const productsAndRevenue = buildProductsWithRevenueByUniqueKey(data);

    return {
      monthYear: key,
      ...productsAndRevenue,
    };
  });
}

function buildProductsWithRevenueByUniqueKey(
  invoices: ItemOf<GetContractBillingScheduleResponse['invoices']>[],
): Record<string, number> {
  return invoices.reduce(
    (result, invoice) => {
      return invoice.products.reduce((productResult, product, productIndex) => {
        validateProductWithOriginalStatus(product);

        const uniqueKey = buildProductUniqueKey(product, productIndex);
        productResult[uniqueKey] = product.revenueBreakdown.total;

        return productResult;
      }, result);
    },
    {} as Record<string, number>,
  );
}

export function buildBillingScheduleDataForInvoices(
  billingScheduleData: GetContractBillingScheduleResponse['invoices'],
) {
  const groupedDataByDate = groupBy(billingScheduleData, (invoice) =>
    vyDate(invoice.invoicingDate).format('MMM YYYY'),
  );

  return getMapEntries(groupedDataByDate).flatMap(([_, currentDateInvoices]) => {
    const sortedInvoices = sortDescending(currentDateInvoices, (invoice) => invoice.revenueBreakdown.total);
    validateNotEmptyArray(sortedInvoices, 'Billing schedule data is empty for the given month/year');

    const firstInvoice = first(sortedInvoices);
    validateNotNullOrUndefined(firstInvoice, 'First invoice is undefined');

    return {
      ...firstInvoice,
      products: sortedInvoices.map((invoice, index) => ({
        productId: invoice.invoiceId + index,
        name: sortedInvoices.length > 1 ? `${invoice.name} (${index + 1})` : invoice.name,
        period: invoice.period,
        revenueBreakdown: invoice.revenueBreakdown,
        originalInvoiceStatus: invoice.reviewStatus,
        originalInvoiceData: {
          id: invoice.invoiceId,
        },
      })),
    };
  });
}
export function buildCompleteTimeline(startDate: Date, endDate: Date): string[] {
  const startMonth = vyDate(startDate).startOf('month');
  const endMonth = vyDate(endDate).endOf('month');

  const monthCount = endMonth.diff(startMonth, 'month') + 1;

  return newArrayOfLength(monthCount).map((_elem, elemIndex) =>
    startMonth.add(elemIndex, 'month').format('MMM YYYY'),
  );
}
